import React from 'react';

import Store from 'store';
import Locale from 'data/locale';

import { OverlayContext } from 'ui/utils/OverlayManager';
import Dialog from 'ui/dialog';

import NumberInput from 'ui/number';
import EditingContext from 'store/editingContext';
import { profileHasImports, profileSetAncient, profileSetStatsPercent, profileSetAugment, profileHasAncients } from 'logic/profileReducers';
import Data from 'data';
import EquipmentSection from './Section';
import Select from 'ui/select';

const _L = Locale("ui-equipment.js");

function EquipmentMod({ title, children, selectRef }) {
  const onClick = React.useCallback(() => selectRef.current.focus(), [selectRef]);
  return (
    <div className="d3p-EquipmentMod">
      <span onClick={onClick}>{title}</span>
      {children}
    </div>
);
}

function UnlockPrompt({ onResolve, onClose }) {
  return (
    <Dialog title={_L`Modify equipment`} modal onClose={onClose}>
      <Dialog.Content>
        <p>{_L`Profile contains imported items. Only enchanted stats will be modified.`}</p>
      </Dialog.Content>
      <Dialog.Footer>
        <button onClick={() => onResolve(false)}>{_L`Ok`}</button>
        <button onClick={() => onResolve(true)}>{_L`Unlock`}</button>
        <button onClick={onClose}>{_L`Cancel`}</button>
      </Dialog.Footer>
    </Dialog>
  );
}

export default React.memo(function EquipmentMods() {
  const overlays = React.useContext(OverlayContext);
  const [augmentLevel, setAugmentLevel] = EditingContext.useState("equipmentModsAugmentLevel", 150);

  const dispatch = Store.useDispatch();
  const follower = (EditingContext.useStateView("paperdollMode") === "follower");
  const hasImports = Store.useProfile(profile => profileHasImports(profile, follower), [follower]);
  const hasAncients = Store.useProfile(profile => profileHasAncients(profile, follower), [follower]);

  const onSetAncient = React.useCallback(option => {
    const value = (option.id === "primal" ? option.id : (option.id === "true"));
    dispatch.setProfile(profile => profileSetAncient(profile, value, follower));
  }, [dispatch, follower]);
  const ancientSelectRef = React.useRef();
  const ancientSelect = (
    <Select value={null} onChange={onSetAncient} options={[
      {id: "false", name: _L`Normal`},
      {id: "true", name: _L`Ancient`},
      {id: "primal", name: _L`Primal Ancient`},
    ]} placeholder={_L`Rarity`} ref={ancientSelectRef}/>
  );

  const onSetPercent = React.useCallback(async percent => {
    let unlock = false;
    if (hasImports) {
      unlock = await overlays.async(<UnlockPrompt/>);
      if (unlock == null) {
        return;
      }
    }
    dispatch.setProfile(profile => profileSetStatsPercent(profile, parseInt(percent), unlock, follower));
  }, [dispatch, overlays, hasImports, follower]);
  const percentSelectRef = React.useRef();
  const percentSelect = (
    <Select value={null} onChange={onSetPercent} options={[...Array(11)].map((_, i) => `${(10 - i) * 10}%`)} placeholder={_L`Percentage`} maxHeight={350} ref={percentSelectRef}/>
  );

  const augmentLevelInput = <NumberInput min={0} max={150} value={augmentLevel} onChange={setAugmentLevel}/>;

  const onSetAugment = React.useCallback(option => {
    dispatch.setProfile(profile => profileSetAugment(profile, option.id ? "caldesanns_" + option.id : null, augmentLevel, follower));
  }, [dispatch, augmentLevel, follower]);
  const augmentStatSelectRef = React.useRef();
  const augmentStatSelect = (
    <Select value={null} onChange={onSetAugment} options={[
      {id: "", name: _L`Remove`},
      {id: "str", name: Data.stats.str.name},
      {id: "dex", name: Data.stats.dex.name},
      {id: "int", name: Data.stats.int.name},
      {id: "vit", name: Data.stats.vit.name},
    ]} placeholder={_L`Attribute`} ref={augmentStatSelectRef} disabled={!hasAncients}/>
  );

  return (
    <EquipmentSection id="equipmentModsExpanded" title={_L`Global equipment modifications`}>
      <EquipmentMod title={_L`Make all items:`} selectRef={ancientSelectRef}>
        {ancientSelect}
      </EquipmentMod>
      <EquipmentMod title={_L`Change all stats to:`} selectRef={percentSelectRef}>
        {percentSelect}
      </EquipmentMod>
      <EquipmentMod title={_L`Add Caldessan's Despair`} selectRef={augmentStatSelectRef}>
        <div className="d3p-caldesanns">
          {augmentLevelInput}
          {augmentStatSelect}
        </div>
      </EquipmentMod>
    </EquipmentSection>
  );
});
